import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Grid,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { apiService, AttractivenessScore, ConsensusData } from '../services/api';
import { useTheme } from '@mui/material/styles';
import ConsensusPanel from '../components/ConsensusPanel';

const StockScoring: React.FC = () => {
  const theme = useTheme();
  const [symbol, setSymbol] = useState('');
  const [duration, setDuration] = useState('6 M');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scoreData, setScoreData] = useState<AttractivenessScore | null>(null);
  const [consensusData, setConsensusData] = useState<ConsensusData | null>(null);
  const [consensusLoading, setConsensusLoading] = useState(false);
  const [batchResults, setBatchResults] = useState<any[]>([]);

  const handleScoreStock = async () => {
    if (!symbol) return;

    setLoading(true);
    setError(null);
    setScoreData(null);
    setConsensusData(null);

    try {
      const data = await apiService.getAttractivenessScore(symbol.toUpperCase(), duration);
      setScoreData(data);

      // Fetch consensus in background (non-blocking — it runs 3 features in parallel on backend)
      setConsensusLoading(true);
      apiService.getConsensus(symbol.toUpperCase())
        .then((c) => setConsensusData(c))
        .catch(() => {/* silently ignore — consensus is bonus info */})
        .finally(() => setConsensusLoading(false));
    } catch (err: any) {
      console.error('Score stock error:', err);
      let errorMessage = 'Failed to fetch score';

      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          errorMessage = err.response.data.detail.map((e: any) => e.msg || JSON.stringify(e)).join(', ');
        } else if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBatchScore = async () => {
    const symbols = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'META', 'NVDA', 'TSLA', 'JPM', 'BAC'];

    setLoading(true);
    setError(null);
    setBatchResults([]); // Clear previous results

    try {
      const data = await apiService.batchScoreStocks(symbols, duration, 10);
      setBatchResults(data.stocks || []);
    } catch (err: any) {
      console.error('Batch score error:', err);
      let errorMessage = 'Failed to fetch batch scores';

      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          // Handle Pydantic validation errors
          errorMessage = err.response.data.detail.map((e: any) => e.msg || JSON.stringify(e)).join(', ');
        } else if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getRecommendationColor = (rec: string) => {
    if (rec.includes('BUY')) return theme.palette.success.main;
    if (rec.includes('SELL')) return theme.palette.error.main;
    return theme.palette.warning.main;
  };

  const getScoreColor = (score: number) => {
    if (score >= 70) return theme.palette.success.main;
    if (score >= 50) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight={700}>
        Stock Attractiveness Scoring
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Comprehensive scoring based on technical indicators, liquidity, and trading signals
      </Typography>

      {/* Search Form */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Stock Symbol"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                onKeyPress={(e) => e.key === 'Enter' && handleScoreStock()}
                placeholder="AAPL"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Timeframe</InputLabel>
                <Select
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  label="Timeframe"
                >
                  <MenuItem value="1 D">1 Day</MenuItem>
                  <MenuItem value="1 W">1 Week</MenuItem>
                  <MenuItem value="1 M">1 Month</MenuItem>
                  <MenuItem value="3 M">3 Months</MenuItem>
                  <MenuItem value="6 M">6 Months</MenuItem>
                  <MenuItem value="1 Y">1 Year</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleScoreStock}
                disabled={loading || !symbol}
                sx={{ height: '56px' }}
              >
                Score Stock
              </Button>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleBatchScore}
                disabled={loading}
                sx={{ height: '56px' }}
              >
                Compare Top Stocks
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Loading / Error */}
      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Single Score Result */}
      {scoreData && !loading && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" fontWeight={600}>{scoreData.symbol}</Typography>
              <Chip
                label={scoreData.recommendation}
                sx={{
                  fontWeight: 600,
                  backgroundColor: getRecommendationColor(scoreData.recommendation) + '20',
                  color: getRecommendationColor(scoreData.recommendation),
                }}
              />
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Typography variant="overline" color="text.secondary">Current Price</Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  ${scoreData.current_price.toFixed(2)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Updated: {new Date(scoreData.timestamp).toLocaleString()}
                </Typography>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="overline" color="text.secondary">Overall Score</Typography>
                <Typography variant="h3" sx={{ color: getScoreColor(scoreData.overall_score), fontWeight: 700 }}>
                  {scoreData.overall_score.toFixed(1)}
                </Typography>
                <Typography variant="caption" color="text.secondary">out of 100</Typography>
                <LinearProgress
                  variant="determinate"
                  value={scoreData.overall_score}
                  sx={{ mt: 1, height: 8, borderRadius: 4 }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="overline" color="text.secondary">Technical</Typography>
                <Typography variant="h3" fontWeight={700}>{scoreData.breakdown.technical.toFixed(1)}</Typography>
                <LinearProgress
                  variant="determinate"
                  value={scoreData.breakdown.technical}
                  sx={{ mt: 1, height: 8, borderRadius: 4 }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="overline" color="text.secondary">Liquidity</Typography>
                <Typography variant="h3" fontWeight={700}>{scoreData.breakdown.liquidity.toFixed(1)}</Typography>
                <LinearProgress
                  variant="determinate"
                  value={scoreData.breakdown.liquidity}
                  sx={{ mt: 1, height: 8, borderRadius: 4 }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="overline" color="text.secondary">Signals</Typography>
                <Typography variant="h3" fontWeight={700}>{scoreData.breakdown.signal.toFixed(1)}</Typography>
                <LinearProgress
                  variant="determinate"
                  value={scoreData.breakdown.signal}
                  sx={{ mt: 1, height: 8, borderRadius: 4 }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>Key Reasons:</Typography>
              {scoreData.reasons.map((reason: string, idx: number) => (
                <Chip key={idx} label={reason} size="small" sx={{ mr: 1, mb: 1 }} />
              ))}
            </Box>

            {/* Timeframe Metadata */}
            {scoreData.recommendation_timeframe && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Recommendation Timeframe
                      </Typography>
                      <Tooltip title="This recommendation is based on historical price patterns and technical indicators analyzed over the specified lookback period. The timeframe indicates how long the recommendation is expected to be relevant.">
                        <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Valid For
                    </Typography>
                    <Chip
                      label={scoreData.recommendation_timeframe}
                      color="primary"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Trading Style
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {scoreData.recommendation_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Lookback Period
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {scoreData.lookback_period}
                    </Typography>
                  </Grid>
                  {scoreData.data_start_date && scoreData.data_end_date && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Data Range: {scoreData.data_start_date} to {scoreData.data_end_date}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Cross-Feature Consensus */}
      {(consensusData || consensusLoading) && scoreData && !loading && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            {consensusLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Running cross-feature consensus…
                </Typography>
              </Box>
            ) : consensusData ? (
              <ConsensusPanel data={consensusData} />
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* Batch Results Table */}
      {batchResults.length > 0 && !loading && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight={600}>Top Stocks Comparison</Typography>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Rank</TableCell>
                        <TableCell>Symbol</TableCell>
                        <TableCell align="right">Current Price</TableCell>
                        <TableCell align="right">Score</TableCell>
                        <TableCell>Recommendation</TableCell>
                        <TableCell align="right">Technical</TableCell>
                        <TableCell align="right">Liquidity</TableCell>
                        <TableCell align="right">Signals</TableCell>
                        <TableCell>Key Reasons</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {batchResults.map((stock, idx) => (
                        <TableRow key={stock.symbol} hover>
                          <TableCell>
                            <Chip
                              label={idx + 1}
                              size="small"
                              color={idx === 0 ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {stock.symbol}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              ${stock.current_price?.toFixed(2) || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              sx={{ color: getScoreColor(stock.score) }}
                            >
                              {stock.score.toFixed(1)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={stock.recommendation}
                              size="small"
                              sx={{
                                backgroundColor: getRecommendationColor(stock.recommendation) + '20',
                                color: getRecommendationColor(stock.recommendation),
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">{stock.breakdown.technical.toFixed(1)}</TableCell>
                          <TableCell align="right">{stock.breakdown.liquidity.toFixed(1)}</TableCell>
                          <TableCell align="right">{stock.breakdown.signal.toFixed(1)}</TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {stock.reasons.slice(0, 2).join(', ')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Explanation Panel */}
          <Grid item xs={12} lg={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Score Components
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Technical Score (40% weight)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Measures trend strength, momentum (RSI, MACD), and volatility. Based on price position relative to moving averages.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label="70-100: Strong" color="success" size="small" />
                    <Chip label="40-69: Moderate" color="warning" size="small" />
                    <Chip label="0-39: Weak" color="error" size="small" />
                  </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Liquidity Score (30% weight)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Reflects trading volume, market activity, and ease of entry/exit. Higher volume indicates better liquidity and lower risk.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label="70-100: High" color="success" size="small" />
                    <Chip label="40-69: Moderate" color="warning" size="small" />
                    <Chip label="0-39: Low (Risky)" color="error" size="small" />
                  </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Signals Score (30% weight)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Based on recent buy/sell signals from technical indicators. More recent buy signals increase score.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label="70-100: Buy Signals" color="success" size="small" />
                    <Chip label="40-69: Mixed" color="warning" size="small" />
                    <Chip label="0-39: Sell Signals" color="error" size="small" />
                  </Box>
                </Box>

                <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Overall Score Recommendations
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                    • 80-100: STRONG BUY - Highly attractive
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                    • 65-79: BUY - Good opportunity
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                    • 50-64: HOLD - Neutral
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                    • 35-49: SELL - Weak fundamentals
                  </Typography>
                  <Typography variant="caption" display="block">
                    • 0-34: STRONG SELL - Very unattractive
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default StockScoring;
